/**
 * StatementPeek — the Courtage-Abrechnung review panel (Story UI-U5, AC3).
 *
 * DecisionPeek host (prev/next over the filtered list, Esc handled by the screen):
 *   Kontrollsumme block → Soll/Ist/Differenz tiles → Belegzeilen rows (+ collapsible
 *   Korrekturgruppen) → line drill-in (Police-Identifikation + Berechtigung check
 *   groups, Soll-Berechnung breakdown, Wertvergleich, Akonto note, Nachweis pins,
 *   latest override reason) → Entscheid bar: EXACTLY the legal actions for the line's
 *   status (transitions + the "Trotzdem freigeben" override with a mandatory reason).
 *
 * Renders SERVER decisions only (design.md §2): status = dot + text, soft badges for
 * category/flags (L1–L4, Differenzklasse). Finance-gated CHF fields arrive null → `—`.
 *
 * [Source: brokly-design-reference/components/brokly/courtage-detail-sheet.tsx] (ported patterns)
 * [Source: docs/stories/ui-u5-courtage-tarifmatrix.md#Tasks — Task 7]
 */

import { useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Check, Coins, Minus, Scale, ShieldCheck, X } from 'lucide-react';
import { DecisionPeek } from '../../components/data/DecisionPeek';
import { StatusDot } from '../../components/ui/StatusDot';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EntityLink } from '../../components/ui/EntityLink';
import { cn } from '../../lib/cn';
import { dateShortDe } from '../../lib/format';
import type { CommissionLineViewDto, CommissionStatementListItemDto } from '@brokly/shared';
import { COMMISSION_LINE_FINDING_LABELS, COURTAGE_NOT_ELIGIBLE_REASON_LABELS } from '@brokly/shared';
import {
  chfOrDash,
  commissionKindLabel,
  deriveLineActions,
  DIFF_CLASS_META,
  diffToneClass,
  factorDisplay,
  lBadge,
  LINE_STATUS_META,
  MIN_OVERRIDE_REASON,
  pctOrDash,
  rateDisplay,
  signedChfOrDash,
  statementStatus,
} from './api/courtage-api';
import { useCourtageStatement, useOverrideLine, useTransitionLine } from './hooks/useCourtage';

interface StatementPeekProps {
  statement: CommissionStatementListItemDto;
  index: number;
  total: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onOpenPolice: (policyId: string) => void;
  onToast: (msg: string) => void;
  onError: (msg: string) => void;
}

const num = 'mono-num tabular-nums';

export function StatementPeek({
  statement,
  index,
  total,
  onClose,
  onPrev,
  onNext,
  onOpenPolice,
  onToast,
  onError,
}: StatementPeekProps) {
  const { data: detail, isLoading, isError } = useCourtageStatement(statement.statement_id);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);

  const st = statementStatus(statement.status);
  const lines = detail?.lines ?? [];
  const selectedLine = selectedLineId ? lines.find((l) => l.line_id === selectedLineId) ?? null : null;

  // Partition lines into Korrekturgruppen vs singles (design: collapsible group blocks).
  const { singles, groups } = useMemo(() => {
    const byGroup = new Map<string, CommissionLineViewDto[]>();
    const singleRows: CommissionLineViewDto[] = [];
    for (const l of lines) {
      if (l.correction_group_id) {
        const arr = byGroup.get(l.correction_group_id) ?? [];
        arr.push(l);
        byGroup.set(l.correction_group_id, arr);
      } else {
        singleRows.push(l);
      }
    }
    return { singles: singleRows, groups: byGroup };
  }, [lines]);

  return (
    <DecisionPeek
      title={`Abrechnung ${statement.carrier_name ?? statement.carrier_uid ?? '-'}`}
      subtitle={`${dateShortDe(statement.period_from)} - ${dateShortDe(statement.period_to)} · ${statement.line_count} Belegzeilen`}
      progress={`${index + 1} / ${total}`}
      width="w-[672px]"
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
      footer={selectedLine ? <EntscheidBar line={selectedLine} onToast={onToast} onError={onError} /> : undefined}
    >
      {isLoading ? (
        <div className="p-4 text-sm text-muted-foreground">Lade Abrechnung …</div>
      ) : isError || !detail ? (
        <div role="alert" className="p-4 text-sm text-muted-foreground">
          Abrechnung konnte nicht geladen werden.
        </div>
      ) : selectedLine ? (
        <LineDetail line={selectedLine} onBack={() => setSelectedLineId(null)} onOpenPolice={onOpenPolice} />
      ) : (
        <div className="flex flex-col gap-4">
          {/* Header status */}
          <div className="flex items-center gap-2">
            <StatusDot tone={st.tone} label={st.label} />
          </div>

          {/* Kontrollsumme */}
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Kontrollsumme</h3>
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <Row label="Summe Belegzeilen (Ist)" value={chfOrDash(detail.sum_lines_ist)} mono />
              <Row label="Ausgewiesenes Gesamttotal" value={chfOrDash(detail.total_amount)} mono border />
              <div
                className={cn(
                  'flex items-center gap-2 border-t border-border px-3 py-2 text-sm',
                  detail.kontrollsumme_ok
                    ? 'bg-status-success-bg text-status-success'
                    : 'bg-status-danger-bg text-status-danger',
                )}
              >
                {detail.kontrollsumme_ok ? <Check className="size-4" /> : <AlertTriangle className="size-4" />}
                {detail.kontrollsumme_ok ? 'Kontrollsumme stimmt' : 'Kontrollsumme weicht ab'}
              </div>
            </div>
          </section>

          {/* Soll / Ist / Differenz tiles */}
          <section className="grid grid-cols-3 gap-2">
            <Tile label="Soll" value={chfOrDash(detail.soll_total)} />
            <Tile label="Ist" value={chfOrDash(detail.ist_total)} />
            <Tile label="Differenz" value={signedChfOrDash(detail.delta_total)} tone={diffToneClass(detail.delta_total)} />
          </section>

          {/* Belegzeilen */}
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Belegzeilen</h3>
            <div className="flex flex-col gap-2">
              {singles.map((l) => (
                <LineRow key={l.line_id} line={l} onOpen={() => setSelectedLineId(l.line_id)} />
              ))}
              {[...groups.entries()].map(([gid, members]) => {
                const grp = detail.correction_groups.find((g) => g.id === gid);
                return (
                  <KorrekturGruppe
                    key={gid}
                    netAmount={grp?.net_amount ?? null}
                    netExpected={grp?.net_expected ?? null}
                    clean={grp?.is_clean_correction ?? false}
                    members={members}
                    onOpen={(id) => setSelectedLineId(id)}
                  />
                );
              })}
              {lines.length === 0 && (
                <div className="rounded-lg border border-border bg-card p-4 text-center text-sm text-muted-foreground">
                  Keine Belegzeilen.
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </DecisionPeek>
  );
}

// ── Belegzeile row ───────────────────────────────────────────────────────────

function LineRow({ line, onOpen }: { line: CommissionLineViewDto; onOpen: () => void }) {
  const status = LINE_STATUS_META[line.line_status];
  const diffMeta = line.is_akonto ? null : line.diff_class ? DIFF_CLASS_META[line.diff_class] : null;
  const l = lBadge(line.match_level);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full flex-col gap-1.5 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:bg-accent/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{line.product_label ?? '-'}</div>
          <div className="truncate text-xs text-muted-foreground">
            <span className={num}>{line.policy_number ?? line.policy_number_raw ?? '-'}</span>
            {' · '}
            {line.kunde_name ?? '-'}
            {' · '}
            {commissionKindLabel(line.commission_kind)}
          </div>
        </div>
        <StatusDot tone={status.tone} label={status.label} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {l && <Badge variant={l.variant}>{l.label}</Badge>}
          {line.is_akonto ? (
            <Badge variant="neutral">Akonto</Badge>
          ) : (
            diffMeta && (
              <Badge variant={diffMeta.variant} title={diffMeta.beschrieb}>
                {diffMeta.label}
              </Badge>
            )
          )}
        </div>
        <div className="text-right text-sm">
          <div className={cn(num, 'text-muted-foreground')}>
            {chfOrDash(line.ist)} <span className="opacity-50">/</span> {chfOrDash(line.soll)}
          </div>
          {!line.is_akonto && (
            <div className={cn(num, 'text-xs font-medium', diffToneClass(line.delta_chf))}>
              {signedChfOrDash(line.delta_chf)} · {pctOrDash(line.delta_pct)}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function KorrekturGruppe({
  netAmount,
  netExpected,
  clean,
  members,
  onOpen,
}: {
  netAmount: string | null;
  netExpected: string | null;
  clean: boolean;
  members: CommissionLineViewDto[];
  onOpen: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 p-3 text-left"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>Korrekturgruppe · {members.length} Zeilen</span>
          {clean ? (
            <Badge variant="success">Netto stimmig</Badge>
          ) : (
            <Badge variant="warning">Netto {signedChfOrDash(netAmount)}</Badge>
          )}
        </div>
        <span className={cn(num, 'text-xs text-muted-foreground')}>
          Netto-Ist {chfOrDash(netAmount)} · Netto-Soll {chfOrDash(netExpected)}
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-2 border-t border-border p-2">
          {members.map((l) => (
            <LineRow key={l.line_id} line={l} onOpen={() => onOpen(l.line_id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Line drill-in ─────────────────────────────────────────────────────────────

function LineDetail({
  line,
  onBack,
  onOpenPolice,
}: {
  line: CommissionLineViewDto;
  onBack: () => void;
  onOpenPolice: (policyId: string) => void;
}) {
  const status = LINE_STATUS_META[line.line_status];
  const berechenbar = line.bemessung !== null || line.soll !== null;
  return (
    <div className="flex flex-col gap-4">
      <button type="button" onClick={onBack} className="w-fit text-xs text-muted-foreground hover:text-foreground">
        ← Zurück zur Abrechnung
      </button>

      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{line.product_label ?? '-'}</div>
          <div className="text-xs text-muted-foreground">
            <span className={num}>{line.policy_number ?? line.policy_number_raw ?? '-'}</span> · {line.kunde_name ?? '-'}
          </div>
        </div>
        <StatusDot tone={status.tone} label={status.label} />
      </div>

      {/* Akonto special case */}
      {line.is_akonto ? (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary p-3 text-sm">
          <Coins className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div>
            <div className="font-medium">Akonto-Vorschuss: keine Differenzprüfung</div>
            <div className="text-muted-foreground">
              Wird mit der definitiven Jahrescourtage verrechnet (siehe Akonto-Saldo).
            </div>
          </div>
        </div>
      ) : null}

      {/* 1 · Police-Identifikation */}
      <CheckGroup title="Police-Identifikation" icon={<ShieldCheck className="size-4 text-muted-foreground" />}>
        <CheckRow
          label="Police zugeordnet"
          state={line.policy_id !== null}
          hint={line.match_stage ? `Match: ${line.match_stage}` : undefined}
        />
        <CheckRow label="Prüfstatus" state={line.check_status === 'matched'} hint={line.check_status ?? undefined} />
      </CheckGroup>

      {/* 2 · Berechtigung */}
      <CheckGroup title="Berechtigung" icon={<ShieldCheck className="size-4 text-muted-foreground" />}>
        <CheckRow label="Mandat gültig" state={line.mandate_valid} />
        <CheckRow label="Police aktiv" state={line.policy_active} />
        <CheckRow label="Broker zugewiesen" state={line.broker_assigned} />
        <CheckRow label="Freigegebene Tarifregel" state={line.approved_tariff_exists} />
        {line.courtage_not_eligible === true && (
          <CheckRow
            label="Courtage erwartet"
            state={false}
            tone="warning"
            hint={
              line.courtage_not_eligible_reason
                ? COURTAGE_NOT_ELIGIBLE_REASON_LABELS[line.courtage_not_eligible_reason]
                : COMMISSION_LINE_FINDING_LABELS.courtage_not_eligible
            }
          />
        )}
      </CheckGroup>

      {/* 3 · Soll-Berechnung */}
      {!line.is_akonto && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <Coins className="size-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Soll-Berechnung</h3>
          </div>
          {berechenbar ? (
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <Row label="Bemessungsgrundlage (Prämie)" value={chfOrDash(line.bemessung)} mono />
              <Row label="Courtagesatz" value={rateDisplay(line.applied_rate)} mono border />
              <Row label="Faktor" value={factorDisplay(line.applied_factor)} mono border />
              <div className="flex items-center justify-between border-t border-border bg-secondary px-3 py-2 text-sm">
                <span className="font-medium">Erwartete Courtage (Soll)</span>
                <span className={cn(num, 'font-semibold')}>{chfOrDash(line.soll)}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-status-warning-border bg-status-warning-bg p-3 text-sm">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-status-warning" />
              <span>Soll nicht berechenbar: {line.soll_status ?? 'keine Tarifregel verfügbar'}.</span>
            </div>
          )}
        </section>
      )}

      {/* 4 · Wertvergleich */}
      {!line.is_akonto && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <Scale className="size-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Wertvergleich · Ist gegen Soll</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Tile label="Erwartet (Soll)" value={chfOrDash(line.soll)} />
            <Tile label="Ausgewiesen (Ist)" value={chfOrDash(line.ist)} tone={diffToneClass(line.delta_chf)} />
          </div>
          <div
            className={cn(
              'flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm',
              diffToneClass(line.delta_chf),
            )}
          >
            <span className="text-muted-foreground">Differenz</span>
            <span className={cn(num, 'font-semibold')}>
              {signedChfOrDash(line.delta_chf)} · {pctOrDash(line.delta_pct)}
            </span>
          </div>
        </section>
      )}

      {/* 5 · Nachweis + override reason */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border border-border bg-card px-3 py-2.5 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Nachweis</span>
        {line.policy_version_id && (
          <span className="inline-flex items-center gap-1">
            <ArrowRight className="size-3" /> Police-Version
          </span>
        )}
        {line.tariff_id && (
          <span className="inline-flex items-center gap-1">
            <ArrowRight className="size-3" /> Tarifregel
          </span>
        )}
        {line.policy_id && (
          <EntityLink className="ml-auto text-xs" onActivate={() => onOpenPolice(line.policy_id!)}>
            Police öffnen
          </EntityLink>
        )}
      </div>

      {line.override_reason && (
        <div className="rounded-lg border border-status-info-border bg-status-info-bg px-3 py-2.5 text-sm text-status-info">
          <span className="font-medium">Begründung ({line.override_actor_kind === 'human' ? 'Mensch' : 'System'}):</span>{' '}
          {line.override_reason}
        </div>
      )}
    </div>
  );
}

// ── Entscheid bar (legal actions + override mandatory reason) ─────────────────

function EntscheidBar({
  line,
  onToast,
  onError,
}: {
  line: CommissionLineViewDto;
  onToast: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const actions = deriveLineActions(line.line_status);
  const transition = useTransitionLine();
  const override = useOverrideLine();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  if (actions.terminal) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-secondary px-3 py-2 text-sm text-muted-foreground">
        <Check className="size-4" /> Zeile abgeschlossen, keine weiteren Entscheide möglich.
      </div>
    );
  }
  if (actions.hint) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-secondary px-3 py-2 text-sm text-muted-foreground">
        <Minus className="size-4" /> {actions.hint}
      </div>
    );
  }

  function runTransition(toStatus: (typeof actions.transitions)[number]['toStatus'], label: string) {
    transition.mutate(
      { lineId: line.line_id, toStatus },
      {
        onSuccess: () => onToast(`${label}: gespeichert`),
        onError: (e) => onError(e instanceof Error ? e.message : 'Aktion fehlgeschlagen'),
      },
    );
  }

  function submitOverride() {
    if (reason.trim().length < MIN_OVERRIDE_REASON) {
      setReasonError(true);
      return;
    }
    override.mutate(
      { lineId: line.line_id, reason: reason.trim(), newStatus: actions.override!.newStatus },
      {
        onSuccess: () => {
          onToast('Freigabe gespeichert');
          setOverrideOpen(false);
          setReason('');
        },
        onError: (e) => onError(e instanceof Error ? e.message : 'Freigabe fehlgeschlagen'),
      },
    );
  }

  const primary = actions.transitions.find((t) => t.tier === 'primary');
  const secondary = actions.transitions.filter((t) => t.tier === 'secondary');
  const menu = actions.transitions.filter((t) => t.tier === 'menu');
  const busy = transition.isPending || override.isPending;

  return (
    <div className="flex flex-col gap-2">
      {overrideOpen && actions.override && (
        <div className="flex flex-col gap-1">
          <label htmlFor="override-reason" className="text-xs font-medium text-muted-foreground">
            Begründung (Pflicht, mind. {MIN_OVERRIDE_REASON} Zeichen)
          </label>
          <textarea
            id="override-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim().length >= MIN_OVERRIDE_REASON) setReasonError(false);
            }}
            rows={2}
            placeholder="Warum wird die Zeile trotz Differenz freigegeben?"
            className={cn(
              'w-full resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
              reasonError ? 'border-status-danger' : 'border-border',
            )}
          />
          {reasonError && (
            <span role="alert" className="text-xs text-status-danger">
              Bitte eine Begründung erfassen (mind. {MIN_OVERRIDE_REASON} Zeichen).
            </span>
          )}
          <div className="mt-1 flex items-center gap-2">
            <Button size="sm" onClick={submitOverride} disabled={busy}>
              Freigabe bestätigen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOverrideOpen(false)} disabled={busy}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {!overrideOpen && (
        <div className="flex flex-wrap items-center gap-2">
          {primary && (
            <Button size="sm" onClick={() => runTransition(primary.toStatus, primary.label)} disabled={busy}>
              {primary.label}
            </Button>
          )}
          {secondary.map((s) => (
            <Button key={s.toStatus} size="sm" variant="outline" onClick={() => runTransition(s.toStatus, s.label)} disabled={busy}>
              {s.label}
            </Button>
          ))}
          {menu.map((m) => (
            <Button key={m.toStatus} size="sm" variant="ghost" onClick={() => runTransition(m.toStatus, m.label)} disabled={busy}>
              {m.label}
            </Button>
          ))}
          {actions.override && (
            <>
              <div className="flex-1" />
              <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setOverrideOpen(true)} disabled={busy}>
                {actions.override.label}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Small building blocks ─────────────────────────────────────────────────────

function Row({ label, value, mono, border }: { label: string; value: string; mono?: boolean; border?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between px-3 py-2 text-sm', border && 'border-t border-border')}>
      <span className="text-muted-foreground">{label}</span>
      <span className={cn(mono && num, 'text-foreground')}>{value}</span>
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-card p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn('text-base font-semibold', num, tone)}>{value}</span>
    </div>
  );
}

function CheckGroup({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="divide-y divide-border rounded-lg border border-border bg-card px-3">{children}</div>
    </section>
  );
}

/** One check line. `tone="warning"`: a mark that does not fail the check (amber), not a red ✗. */
function CheckRow({
  label,
  state,
  hint,
  tone,
}: {
  label: string;
  state: boolean | null;
  hint?: string;
  tone?: 'warning';
}) {
  const icon =
    state === false && tone === 'warning' ? (
      <AlertTriangle className="size-4 text-status-warning" />
    ) : state === true ? (
      <Check className="size-4 text-status-success" />
    ) : state === false ? (
      <X className="size-4 text-status-danger" />
    ) : (
      <Minus className="size-4 text-muted-foreground" />
    );
  return (
    <div className="flex items-center justify-between gap-2 py-2 text-sm">
      <span className="flex items-center gap-2">
        {icon}
        {label}
      </span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}
